import React, { createContext, useEffect, useState, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Layout from './components/Layout';
import RootGate from './components/RootGate';
import ProtectedRoute from './components/ProtectedRoute';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import ApiTest from './components/ApiTest';
import SplashScreen from './components/SplashScreen';
import BannerDrawer from './components/BannerDrawer';
import { AuthProvider } from './contexts/AuthContext';
import { SnackbarProvider } from './contexts/SnackbarContext';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import { initializeMobileOptimizations } from './utils/mobileUtils';
import pwaService from './services/pwaService';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, CircularProgress, Box } from '@mui/material';
import { ConfigProvider } from 'antd';
import theme from './themes/theme';
import colors from './themes/colors';
import { ApiUrlValidator } from './utils/apiUrlValidator';
import { validateEnvironmentOnStartup } from './utils/EnvironmentValidator';
import { apiUrlManager } from './utils/ApiUrlManager';

// 📌 코드 스플리팅 적용 전 메인 번들 사이즈: 1.01 MB
// React.lazy를 사용한 코드 스플리팅으로 번들 사이즈 최적화

// 일반 사용자 페이지들 - Lazy Loading 적용
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Login = React.lazy(() => import('./pages/main/Login'));
const Consent = React.lazy(() => import('./pages/main/Consent'));
const EmailVerification = React.lazy(() => import('./pages/main/EmailVerification'));
const AccountInfo = React.lazy(() => import('./pages/main/AccountInfo'));
const RegistrationPathSelect = React.lazy(() => import('./pages/main/RegistrationPathSelect'));
const BusinessInfo = React.lazy(() => import('./pages/main/BusinessInfo'));
const Register = React.lazy(() => import('./pages/Register'));
const ForgotPassword = React.lazy(() => import('./pages/main/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const Orders = React.lazy(() => import('./pages/Orders'));
const Menus = React.lazy(() => import('./pages/Menus'));
const Inventory = React.lazy(() => import('./pages/Inventory'));
const Analytics = React.lazy(() => import('./pages/Analytics'));
const StoreSettings = React.lazy(() => import('./pages/StoreSettings'));
const Profile = React.lazy(() => import('./pages/Profile'));
const SessionRenewal = React.lazy(() => import('./pages/SessionRenewal'));
const ServerMaintenance = React.lazy(() => import('./pages/ServerMaintenance'));
const PlanSelection = React.lazy(() => import('./pages/main/PlanSelection'));
const InstallationRequest = React.lazy(() => import('./pages/main/InstallationRequest'));
const CardRegistration = React.lazy(() => import('./pages/main/CardRegistration'));
const MainPage = React.lazy(() => import('./pages/main/MainPage'));
const RegistrationComplete = React.lazy(() => import('./pages/main/RegistrationComplete'));
const RegistrationCompleteExternal = React.lazy(() => import('./pages/main/RegistrationCompleteExternal'));
const ContinueRegistration = React.lazy(() => import('./pages/main/ContinueRegistration'));
const PendingApproval = React.lazy(() => import('./pages/main/PendingApproval'));
const PendingApprovalExternal = React.lazy(() => import('./pages/main/PendingApprovalExternal'));
const SubscriptionStartsOn = React.lazy(() => import('./pages/main/SubscriptionStartsOn'));
const InstallationWaiting = React.lazy(() => import('./pages/InstallationWaiting'));

// 다중 매장 페이지들 (TODO 2.1)
const MultiStoreLayout = React.lazy(() => import('./pages/multi/MultiStoreLayout'));
const MultiStoreDashboard = React.lazy(() => import('./pages/multi/MultiStoreDashboard'));
const MultiStoreStores = React.lazy(() => import('./pages/multi/MultiStoreStores'));
const MultiStoreInsightsLegacyRedirect = React.lazy(() => import('./pages/multi/MultiStoreInsightsLegacyRedirect'));

// 관리자 페이지들 - Lazy Loading 적용
const AdminLayout = React.lazy(() => import('./components/admin/AdminLayout'));
const AdminLogin = React.lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const AdminStores = React.lazy(() => import('./pages/admin/AdminStores'));
const AdminInstallationList = React.lazy(() => import('./pages/admin/AdminInstallationList'));
const AdminSubscriptions = React.lazy(() => import('./pages/admin/AdminSubscriptions'));
const AdminSubscriptionDashboard = React.lazy(() => import('./pages/admin/AdminSubscriptionDashboard'));
const AdminPayments = React.lazy(() => import('./pages/admin/AdminPayments'));
const AdminPaymentFailures = React.lazy(() => import('./pages/admin/AdminPaymentFailures'));
const AdminSuspendedAccounts = React.lazy(() => import('./pages/admin/AdminSuspendedAccounts'));
const AdminInstallationSchedule = React.lazy(() => import('./pages/admin/AdminInstallationSchedule'));
const AdminBusinessInfo = React.lazy(() => import('./pages/admin/AdminBusinessInfo'));
const AdminLogs = React.lazy(() => import('./pages/admin/AdminLogs'));
const AdminFailedOrders = React.lazy(() => import('./pages/admin/AdminFailedOrders'));
const AdminUserApprovals = React.lazy(() => import('./pages/admin/AdminUserApprovals'));
const AdminPushNotificationSettings = React.lazy(() => import('./pages/admin/AdminPushNotificationSettings'));
const AdminPushNotification = React.lazy(() => import('./pages/admin/AdminPushNotification'));
const AdminScheduledPushNotification = React.lazy(() => import('./pages/admin/AdminScheduledPushNotification'));
const DailyReport = React.lazy(() => import('./pages/DailyReport'));
const AdminBanner = React.lazy(() => import('./pages/admin/AdminBanner'));
const AdminPromotions = React.lazy(() => import('./pages/admin/AdminPromotions'));
const AdminPartners = React.lazy(() => import('./pages/admin/AdminPartners'));
const AdminApiMonitoring = React.lazy(() => import('./pages/admin/AdminApiMonitoring'));
const AdminApiUsage = React.lazy(() => import('./pages/admin/AdminApiUsage'));
const AdminServerManagement = React.lazy(() => import('./pages/admin/AdminServerManagement'));
const AdminSystemHealth = React.lazy(() => import('./pages/admin/AdminSystemHealth'));
const AdminDataIntegrity = React.lazy(() => import('./pages/admin/AdminDataIntegrity'));
const AdminDailyReportCostBasis = React.lazy(() => import('./pages/admin/AdminDailyReportCostBasis'));
const AdminSecurityEvents = React.lazy(() => import('./pages/admin/AdminSecurityEvents'));
const AdminBackupManagement = React.lazy(() => import('./pages/admin/AdminBackupManagement'));
const AdminSystemSettings = React.lazy(() => import('./pages/admin/AdminSystemSettings'));
const AdminProfile = React.lazy(() => import('./pages/admin/AdminProfile'));
const AdminBridgeDiagnostics = React.lazy(() => import('./pages/admin/AdminBridgeDiagnostics'));

// 로딩 컴포넌트
const LoadingFallback = () => (
  <Box 
    display="flex" 
    justifyContent="center" 
    alignItems="center" 
    minHeight="60vh"
    flexDirection="column"
    gap={2}
  >
    <CircularProgress size={40} />
    <div style={{ fontSize: '14px', color: '#666' }}>페이지를 불러오는 중...</div>
  </Box>
);

// 스크롤 위치 초기화 컴포넌트
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // 페이지 이동 시 스크롤을 맨 위로 이동
    // 여러 스크롤 컨테이너를 순차적으로 시도
    const selectors = [
      'main', // Layout의 메인 컨테이너 (component="main")
      'body', // body 요소
      'html' // html 요소
    ];
    
    for (const selector of selectors) {
      const element = document.querySelector(selector) as HTMLElement;
      if (element && element.scrollTop > 0) {
        element.scrollTo(0, 0);
        break;
      }
    }
  }, [pathname]);

  return null;
};

// 접근성 관련 컨텍스트
export const AccessibilityContext = createContext<{
  fixAriaHiddenOnRoot: () => void;
}>({
  fixAriaHiddenOnRoot: () => {}
});

// 라우트 변경에 따른 manifest 관리 컴포넌트 (Router 안에서 동작)
const ManifestManager: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const currentPath = location.pathname;
    const isAdminPath = currentPath.startsWith('/admin');
    let manifestLink = document.querySelector('link[rel="manifest"]') as HTMLLinkElement;
    
    if (isAdminPath) {
      // 관리자 페이지일 때는 즉시 관리자 manifest 사용
      if (!manifestLink) {
        // manifest 링크가 없으면 생성
        manifestLink = document.createElement('link');
        manifestLink.rel = 'manifest';
        document.head.appendChild(manifestLink);
      }
      
      // 관리자 manifest로 변경
      if (!manifestLink.href.includes('manifest-admin.json')) {
        manifestLink.href = '/manifest-admin.json';
        console.log('📱 [PWA] 관리자 페이지 manifest 변경: manifest-admin.json');
      }
    } else {
      // 관리자 페이지가 아닐 때는 기본 manifest로 복원
      if (manifestLink && manifestLink.href.includes('manifest-admin.json')) {
        manifestLink.href = '/manifest.json';
        console.log('📱 [PWA] 일반 페이지 manifest 복원: manifest.json');
      }
    }
  }, [location.pathname]);

  return null;
};

const AppContent: React.FC = () => {
  const [showSplash, setShowSplash] = useState(true);
  const [showBanner, setShowBanner] = useState(false);

  // 모달이 열릴 때 root 요소에 aria-hidden="true"가 적용되는 문제 해결
  const fixAriaHiddenOnRoot = () => {
    const rootElement = document.getElementById('root');
    if (rootElement && rootElement.getAttribute('aria-hidden') === 'true') {
      rootElement.removeAttribute('aria-hidden');
    }
  };

  // 스플래시 화면 완료 처리
  const handleSplashComplete = () => {
    setShowSplash(false);
    // 스플래시 완료 후 배너 표시
    setShowBanner(true);
  };

  // 앱 초기화
  useEffect(() => {
    // 모바일 최적화 초기화
    initializeMobileOptimizations();

    // PWA 서비스워커 등록은 index.tsx에서 처리됨 (중복 방지)

    // 개발 모드에서 API URL 자동 검증 (한 번만 실행)
    if (process.env.NODE_ENV === 'development') {
      ApiUrlValidator.getConsoleHelpers().autoValidate();
    }

    // 환경 검증 및 API URL 관리
    validateEnvironmentOnStartup();
    apiUrlManager.preloadAllUrls();
    apiUrlManager.validateAllUrls();
  }, []);

  // navigate 함수 등록 (Layout.tsx보다 먼저 실행되도록)
  useEffect(() => {
    const registerNavigateFunction = () => {
      // React Router와 완전 호환되는 안전한 navigate 함수 등록
      (window as any).__navigate = (url: string) => {
        console.log('🔗 [App] 직접 navigate 함수 호출:', url);
        console.log('🔗 [App] 현재 URL:', window.location.href);
        console.log('🔗 [App] 대상 URL:', url);
        
        // React Router와 완전 호환되는 안전한 네비게이션
        try {
          // 방법 1: React Router의 내부 메커니즘 활용
          // React Router는 history API를 사용하므로 직접 조작하지 말고
          // React Router가 제공하는 방법을 사용
          
          // 현재 경로와 대상 경로가 다른 경우에만 이동
          if (window.location.pathname !== url) {
            console.log('🔄 [App] 경로 변경 필요:', window.location.pathname, '→', url);
            
            // React Router의 history 객체를 직접 사용
            if (window.history && window.history.pushState) {
              // 1. URL 변경 (세션 유지)
              window.history.pushState(null, '', url);
              console.log('✅ [App] history.pushState 완료');
              
              // 2. React Router가 변경을 감지하도록 이벤트 발생
              // React Router는 popstate 이벤트를 감지하므로 정확히 발생시켜야 함
              const popStateEvent = new PopStateEvent('popstate', {
                state: null,
                bubbles: true,
                cancelable: true
              });
              window.dispatchEvent(popStateEvent);
              console.log('✅ [App] popstate 이벤트 발생 완료');
              
              // 3. 추가 안전장치: React Router의 내부 상태 강제 업데이트
              // React Router가 변경을 놓쳤을 경우를 대비한 추가 이벤트
              setTimeout(() => {
                if (window.location.pathname === url) {
                  console.log('✅ [App] 네비게이션 성공 확인');
                } else {
                  console.log('⚠️ [App] 네비게이션 실패 - 폴백 실행');
                  window.location.href = url;
                }
              }, 100);
              
            } else {
              console.log('⚠️ [App] history API 미지원 - 기본 동작 사용');
              window.location.href = url;
            }
          } else {
            console.log('✅ [App] 이미 해당 페이지에 있음 - 포커스만');
            window.focus();
          }
          
        } catch (error) {
          console.error('❌ [App] navigate 함수 실행 중 오류:', error);
          console.error('❌ [App] 오류 상세:', error);
          // 오류 발생 시 기본 동작으로 폴백 (세션 유지)
          console.log('🔄 [App] 오류 발생 - 기본 동작으로 폴백');
          window.location.href = url;
        }
      };
      console.log('✅ [App] 직접 navigate 함수 등록 완료');
    };
    
    // 즉시 navigate 함수 등록
    registerNavigateFunction();

    // Service Worker 메시지 리스너 등록
    const handleServiceWorkerMessage = async (event: MessageEvent) => {
      console.log('📨 [App] Service Worker 메시지 수신:', event.data);
      console.log('📨 [App] 현재 URL:', window.location.href);
      console.log('📨 [App] 현재 시간:', new Date().toISOString());
      
      if (event.data?.type === 'DIRECT_NAVIGATION') {
        console.log('🔔 [App] 직접 페이지 이동 요청 (사용하지 않음):', event.data);
        // Service Worker에서 새 탭으로 열기 때문에 이 코드는 실행되지 않음
      } else if (event.data?.type === 'OPEN_EXTERNAL_URL') {
        // 외부 URL 열기 (안드로이드 호환성)
        const targetUrl = event.data.url;
        console.log('🌐 [App] 외부 URL 열기 요청:', targetUrl);
        try {
          window.open(targetUrl, '_blank', 'noopener,noreferrer');
          window.focus();
        } catch (error) {
          console.error('❌ [App] 외부 URL 열기 실패:', error);
          // 폴백: 현재 창에서 열기
          window.location.href = targetUrl;
        }
      } else if (event.data?.type === 'NOTIFICATION_CLICKED') {
        console.log('🔔 [App] 알림 클릭 이벤트 처리:', event.data);
        console.log('🔔 [App] 알림 액션:', event.data.action);
        console.log('🔔 [App] 알림 데이터:', event.data.data);
        
        // 알림 데이터에서 URL 추출 및 내부 경로 정규화 (앞에 / 없으면 추가)
        let targetUrl = (event.data.data?.url || '/').toString().trim() || '/';
        if (!targetUrl.startsWith('http://') && !targetUrl.startsWith('https://') && !targetUrl.startsWith('/')) {
          targetUrl = '/' + targetUrl;
        }
        console.log('🔗 [App] 이동할 URL:', targetUrl);
        console.log('🔗 [App] 현재 경로:', window.location.pathname);
        
        // 외부 링크인지 확인 (http:// 또는 https://로 시작하는 경우)
        const isExternalUrl = targetUrl.startsWith('http://') || targetUrl.startsWith('https://');
        console.log('🔗 [App] 외부 링크 여부:', isExternalUrl);
        
        // 외부 링크인 경우 새 탭으로 열기
        if (isExternalUrl) {
          console.log('🌐 [App] 외부 링크 - 새 탭으로 열기:', targetUrl);
          try {
            window.open(targetUrl, '_blank', 'noopener,noreferrer');
            window.focus();
          } catch (error) {
            console.error('❌ [App] 외부 링크 열기 실패:', error);
            // 폴백: 현재 창에서 열기
            window.location.href = targetUrl;
          }
          return;
        }
        
        // 내부 경로인 경우 기존 로직 사용
        // 세션 유지를 위한 안전한 페이지 이동
        try {
          console.log('🔍 [App] 페이지 이동 전 상태 확인');
          console.log('🔍 [App] window.__navigate 존재:', !!(window as any).__navigate);
          console.log('🔍 [App] 현재 경로와 대상 경로 비교:', window.location.pathname, 'vs', targetUrl);
          console.log('🔍 [App] 세션 유지 플래그:', event.data.preserveSession);
          
          // 세션 유지를 위한 안전한 네비게이션
          if (window.location.pathname !== targetUrl) {
            console.log('🔄 [App] 현재 창에서 페이지 이동:', targetUrl);
            
            // navigate 함수 사용 (세션 유지)
            if (typeof window !== 'undefined' && (window as any).__navigate) {
              console.log('✅ [App] navigate 함수 사용 (세션 유지)');
              console.log('✅ [App] navigate 함수 호출 전');
              
              try {
                (window as any).__navigate(targetUrl);
                console.log('✅ [App] navigate 함수 호출 완료');
              } catch (navigateError) {
                console.error('❌ [App] navigate 함수 호출 실패:', navigateError);
                // navigate 실패 시 history API로 폴백
                console.log('🔄 [App] History API로 폴백');
                window.history.pushState(null, '', targetUrl);
                window.dispatchEvent(new PopStateEvent('popstate'));
              }
            } else {
              // navigate 함수가 없으면 history API 사용 (세션 유지)
              console.log('✅ [App] History API 사용 (세션 유지)');
              console.log('✅ [App] history.pushState 호출 전');
              window.history.pushState(null, '', targetUrl);
              console.log('✅ [App] history.pushState 호출 후');
              // React Router가 변경을 감지하도록 popstate 이벤트 발생
              console.log('✅ [App] popstate 이벤트 발생');
              window.dispatchEvent(new PopStateEvent('popstate'));
              console.log('✅ [App] popstate 이벤트 발생 완료');
            }
          } else {
            console.log('✅ [App] 이미 해당 페이지에 있음 - 포커스만');
            window.focus();
          }
        } catch (error) {
          console.error('❌ [App] 페이지 이동 중 오류:', error);
          console.error('❌ [App] 오류 스택:', error instanceof Error ? error.stack : '스택 정보 없음');
          // 오류 발생 시 기본 동작 (세션 유지)
          console.log('🔄 [App] 오류 발생 - 기본 동작으로 폴백');
          window.focus();
        }
      }
    };

    // Service Worker 메시지 리스너 등록
    navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);

    // 접근성 관련 설정
    const rootElement = document.getElementById('root');
    if (!rootElement) {
      // rootElement가 없으면 Service Worker만 정리
      return () => {
        navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
      };
    }

    // TypeScript null 체크를 위한 타입 가드
    const safeRootElement = rootElement as HTMLElement;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'aria-hidden' &&
          safeRootElement.getAttribute('aria-hidden') === 'true'
        ) {
          safeRootElement.removeAttribute('aria-hidden');
        }
      });
    });

    // safeRootElement 사용
    observer.observe(safeRootElement, { attributes: true });

    // 정리 함수 - Service Worker와 Observer 모두 정리
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
      observer.disconnect();
    };
  }, []);

  // 기본 테마 설정
  const customization = {
    borderRadius: colors.radius.card,
    navType: 'light' as 'light' | 'dark'
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: colors.primary.main,
          colorSuccess: colors.success.main,
          colorWarning: colors.warning.main,
          colorError: colors.error.main,
          colorInfo: colors.info.main,
          colorText: colors.text.primary,
          colorTextSecondary: colors.text.secondary,
          colorBorder: colors.borderTokens.default,
          colorBgContainer: colors.surface.default,
          colorBgLayout: colors.surface.canvas,
          borderRadius: colors.radius.control,
          boxShadow: colors.shadow.low,
          boxShadowSecondary: colors.shadow.high,
          fontFamily:
            '"Pretendard Variable", Pretendard, -apple-system, BlinkMacSystemFont, system-ui, Roboto, "Helvetica Neue", "Segoe UI", "Apple SD Gothic Neo", "Noto Sans KR", "Malgun Gothic", sans-serif'
        }
      }}
    >
    <ThemeProvider theme={theme()}>
      <CssBaseline />
      <AccessibilityContext.Provider value={{ fixAriaHiddenOnRoot }}>
        <AuthProvider>
          <AdminAuthProvider>
            <SnackbarProvider>
              {showSplash ? (
                <SplashScreen onComplete={handleSplashComplete} />
              ) : (
              <>
              {showBanner && (
                <BannerDrawer onClose={() => setShowBanner(false)} />
              )}
              <Router>
                <ScrollToTop />
                <ManifestManager />
                <Suspense fallback={<LoadingFallback />}>
                  <Routes>
                    {/* 일반 사용자 라우트 */}
                    <Route path="/" element={<MainPage />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/consent" element={<Consent />} />
                    <Route path="/email-verification" element={<EmailVerification />} />
                    <Route path="/emailverification" element={<Navigate to="/email-verification" replace />} />
                    <Route path="/account-info" element={<AccountInfo />} />
                    <Route path="/registration-path-select" element={<RegistrationPathSelect />} />
                    <Route path="/business-info" element={<BusinessInfo />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/register/partners/:code" element={<Register />} />
                    <Route path="/plan-selection" element={<PlanSelection />} />
                    <Route path="/installation-request" element={<InstallationRequest />} />
                    <Route path="/card-registration" element={<CardRegistration />} />
                    <Route path="/registration-complete" element={<RegistrationComplete />} />
                    <Route path="/registration-complete-external" element={<RegistrationCompleteExternal />} />
                    <Route path="/continue-registration" element={<ContinueRegistration />} />
                    <Route path="/pending-approval" element={<PendingApproval />} />
                    <Route path="/pending-approval-external" element={<PendingApprovalExternal />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/session-renewal" element={<SessionRenewal />} />
                    <Route path="/server-maintenance" element={<ServerMaintenance />} />
                    <Route path="/api-test" element={<ApiTest />} />
                    <Route path="/*" element={<ProtectedRoute />}>
                      <Route path="subscription-starts-on" element={<SubscriptionStartsOn />} />
                      <Route path="*" element={<Layout />}>
                        <Route path="dashboard" element={<Dashboard />} />
                        <Route path="orders" element={<Orders />} />
                        <Route path="menus" element={<Menus />} />
                        <Route path="inventory" element={<Inventory />} />
                        <Route path="analytics" element={<Analytics />} />
                        <Route path="settings" element={<StoreSettings />} />
                        <Route path="profile" element={<Profile />} />
                        <Route path="reports/daily" element={<DailyReport />} />
                        <Route path="installation-waiting" element={<InstallationWaiting />} />
                        {/* 다중 매장: catch-all 앞에 배치 (TODO 2.1) */}
                        <Route path="multi-store" element={<MultiStoreLayout />}>
                          <Route index element={<Navigate to="/multi-store/dashboard" replace />} />
                          <Route path="dashboard" element={<MultiStoreDashboard />} />
                          <Route path="performance" element={<Navigate to="/multi-store/dashboard#performance" replace />} />
                          <Route path="insights" element={<MultiStoreInsightsLegacyRedirect basePath="" />} />
                          <Route path="stores" element={<MultiStoreStores />} />
                          <Route path="secondary" element={<Navigate to="/multi-store/dashboard#secondary" replace />} />
                          <Route path="secondary/goal-vs-actual" element={<Navigate to="/multi-store/dashboard#goal-vs-actual" replace />} />
                          <Route path="secondary/trends" element={<Navigate to="/multi-store/dashboard#trends" replace />} />
                          <Route path="secondary/cost-margin" element={<Navigate to="/multi-store/dashboard#cost-margin" replace />} />
                          <Route path="secondary/benchmark" element={<Navigate to="/multi-store/dashboard#benchmark" replace />} />
                          <Route path="secondary/promo" element={<Navigate to="/multi-store/dashboard#promo" replace />} />
                          <Route path="secondary/franchise-usage" element={<Navigate to="/multi-store/dashboard#franchise-usage" replace />} />
                          <Route path="secondary/report" element={<Navigate to="/multi-store/dashboard#report" replace />} />
                          <Route path="secondary/export" element={<Navigate to="/multi-store/dashboard#export" replace />} />
                          <Route path="*" element={<Navigate to="/multi-store/dashboard" replace />} />
                        </Route>
                        <Route path="*" element={<Navigate to="/dashboard" replace />} />
                      </Route>
                    </Route>

                    {/* 관리자 라우트 */}
                    <Route path="/admin/*" element={
                      <Routes>
                        <Route path="login" element={<AdminLogin />} />
                        <Route path="*" element={
                          <AdminProtectedRoute>
                            <AdminLayout />
                          </AdminProtectedRoute>
                        }>
                          <Route path="dashboard" element={<AdminDashboard />} />
                          <Route path="user-approvals" element={<AdminUserApprovals />} />
                          <Route path="stores" element={<AdminStores />} />
                          <Route path="installation-list" element={<AdminInstallationList />} />
                          <Route path="installation-schedule" element={<AdminInstallationSchedule />} />
                          <Route path="subscriptions" element={<AdminSubscriptions />} />
                          <Route path="promotions" element={<AdminPromotions />} />
                          <Route path="partners" element={<AdminPartners />} />
                          <Route path="subscription-dashboard" element={<AdminSubscriptionDashboard />} />
                          <Route path="payments" element={<AdminPayments />} />
                          <Route path="payment-failures" element={<AdminPaymentFailures />} />
                          <Route path="suspended-accounts" element={<AdminSuspendedAccounts />} />
                          <Route path="logs" element={<AdminLogs />} />
                          <Route path="failed-orders" element={<AdminFailedOrders />} />
                          <Route path="bridge-diagnostics" element={<AdminBridgeDiagnostics />} />
                          <Route path="security" element={<AdminSecurityEvents />} />
                          <Route path="api-usage" element={<AdminApiUsage />} />
                          <Route path="api-monitoring" element={<AdminApiMonitoring />} />
                          <Route path="system-health" element={<AdminSystemHealth />} />
                          <Route path="data-integrity" element={<AdminDataIntegrity />} />
                          <Route path="daily-report-cost-basis" element={<AdminDailyReportCostBasis />} />
                          <Route path="backups" element={<AdminBackupManagement />} />
                          <Route path="push-notification" element={<AdminPushNotification />} />
                          <Route path="push-notification-settings" element={<AdminPushNotificationSettings />} />
                          <Route path="scheduled-push" element={<AdminScheduledPushNotification />} />
                          <Route path="banner" element={<AdminBanner />} />
                          <Route path="business-info" element={<AdminBusinessInfo />} />
                          <Route path="settings" element={<AdminSystemSettings />} />
                          <Route path="server-management" element={<AdminServerManagement />} />
                          <Route path="profile" element={<AdminProfile />} />
                          <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
                        </Route>
                      </Routes>
                    } />
                  </Routes>
                </Suspense>
              </Router>
              </>
              )}
            </SnackbarProvider>
          </AdminAuthProvider>
        </AuthProvider>
      </AccessibilityContext.Provider>
    </ThemeProvider>
    </ConfigProvider>
  );
};

const App: React.FC = () => {
  return <AppContent />;
};

export default App; 